import { Button, Card, Divider, Input, Modal, Space, Switch, Tabs, Typography } from 'antd';
import TabPane from 'antd/es/tabs/TabPane';
import Title from 'antd/es/typography/Title';
import { v4 as uuidv4 } from 'uuid';
import Overview from './Overview';
import RiskArea from './RiskArea';
import Corsia from './Corsia';
import Scope from './Scope';
import { useState } from 'react';

interface MethodologiesProps {
  data: any;
  setData: (path: string, value: any) => void;
  activeTab: string;
  readonly?: boolean;
}

const Methodologies: React.FC<MethodologiesProps> = ({
  data,
  setData,
  activeTab,
  readonly = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [methodologyToRemove, setMethodologyToRemove] = useState<string | null>(null);

  const moveMethodology = (fromIndex: number, toIndex: number) => {
    if (readonly) return;
    setData('methodologies.reorder', { fromIndex, toIndex });
  };

  const handleRemoveMethodology = (methodologyId: string) => {
    if (readonly) return;
    setMethodologyToRemove(methodologyId);
    setIsModalOpen(true);
  };

  const handleConfirmRemoveMethodology = () => {
    if (readonly) return;
    if (methodologyToRemove) {
      const updatedMethodologies = data.data.filter((m) => m.id !== methodologyToRemove);
      setData('methodologies.data', updatedMethodologies);
    }
    setIsModalOpen(false);
  };

  const handleCancelRemoveMethodology = () => {
    setIsModalOpen(false);
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Title level={5}>Methodologies</Title>
      <Card>
        {data.data.map((methodology, index) => (
          <Space direction="vertical" style={{ width: '100%', marginBottom: '20px' }}>
            <Space
              direction="horizontal"
              style={{ justifyContent: 'space-between', width: '100%' }}>
              <Typography.Title level={5}>Methodology no. {index + 1}</Typography.Title>
              <Space>
                <Button
                  size="small"
                  disabled={readonly || index === 0}
                  onClick={() => moveMethodology(index, index - 1)}>
                  ↑
                </Button>
                <Button
                  size="small"
                  disabled={readonly || index === data.data.length - 1}
                  onClick={() => moveMethodology(index, index + 1)}>
                  ↓
                </Button>
                <Button
                  type="primary"
                  danger
                  style={{ marginTop: '10px' }}
                  onClick={() => handleRemoveMethodology(methodology.id)}
                  disabled={readonly}>
                  Remove
                </Button>
              </Space>
            </Space>
            <Input
              placeholder="Standard"
              value={methodology.standard}
              onChange={(e) => {
                if (readonly) return;
                const updatedMethodologies = data.data.map((m) => {
                  if (m.id === methodology.id) {
                    return { ...m, standard: e.target.value };
                  }
                  return m;
                });
                setData('methodologies.data', updatedMethodologies);
              }}
              disabled={readonly}
            />
            <Input
              placeholder="Methodology name"
              value={methodology.methodology}
              onChange={(e) => {
                if (readonly) return;
                const updatedMethodologies = data.data.map((m) => {
                  if (m.id === methodology.id) {
                    return { ...m, methodology: e.target.value };
                  }
                  return m;
                });
                setData('methodologies.data', updatedMethodologies);
              }}
              disabled={readonly}
            />
            <Input
              placeholder="Version"
              value={methodology.version}
              onChange={(e) => {
                if (readonly) return;
                const updatedMethodologies = data.data.map((m) => {
                  if (m.id === methodology.id) {
                    return { ...m, version: e.target.value };
                  }
                  return m;
                });
                setData('methodologies.data', updatedMethodologies);
              }}
              disabled={readonly}
            />
          </Space>
        ))}
        <Button
          type="primary"
          style={{ marginTop: '10px' }}
          onClick={() => {
            if (readonly) return;
            setData('methodologies.data', [
              ...data.data,
              { id: uuidv4(), standard: '', methodologyName: '', input: '' }
            ]);
          }}
          disabled={readonly}>
          Add Methodology
        </Button>
        <Divider />
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text strong>Add Scope (toggle this to show the scope tab):</Typography.Text>
          <Switch
            checked={data.hasScope}
            onChange={(checked) => setData('methodologies.hasScope', checked)}
            disabled={readonly}
          />
        </Space>
      </Card>

      <Tabs defaultActiveKey="overview" type="card">
        <TabPane tab="Overview" key="overview">
          <Card>
            <Overview data={data} setData={setData} readonly={readonly} />
          </Card>
        </TabPane>
        {data.hasScope && (
          <TabPane tab="Scope" key="scope">
            <Card>
              <Scope data={data} setData={setData} readonly={readonly} />
            </Card>
          </TabPane>
        )}
        <TabPane tab="Additionality" key="additionality">
          <Card>
            <RiskArea data={data} setData={setData} activeTab="additionality" readonly={readonly} />
          </Card>
        </TabPane>
        <TabPane tab="Over-crediting" key="overCrediting">
          <Card>
            <RiskArea data={data} setData={setData} activeTab="overCrediting" readonly={readonly} />
          </Card>
        </TabPane>
        <TabPane tab="Permanence" key="permanence">
          <Card>
            <RiskArea data={data} setData={setData} activeTab="permanence" readonly={readonly} />
          </Card>
        </TabPane>
        <TabPane tab="Overlapping Claims" key="overLappingClaims">
          <Card>
            <RiskArea
              data={data}
              setData={setData}
              activeTab="overLappingClaims"
              readonly={readonly}
            />
          </Card>
        </TabPane>
        <TabPane tab="Corsia" key="corsia">
          <Card>
            <Corsia data={data} setData={setData} readonly={readonly} />
          </Card>
        </TabPane>
      </Tabs>
      <Modal
        title="Confirm removal of methodology"
        open={isModalOpen}
        onOk={handleConfirmRemoveMethodology}
        onCancel={handleCancelRemoveMethodology}>
        All the tabs will be affected, are you sure you want to remove this methodology?
      </Modal>
    </Space>
  );
};

export default Methodologies;
